import type { ReactNode } from 'react'
import { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { AppShell } from '@/components/layout/AppShell'
import { HiddenSimulationWidget } from '@/components/admin/HiddenSimulationWidget'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { ErrorState, LoadingState } from '@/components/common/StateViews'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
import { useBootstrap } from '@/hooks/useBootstrap'
import { useAuthStore } from '@/store'

// Route-level code splitting keeps the first paint small on mobile data.
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const PlansPage = lazy(() => import('@/pages/PlansPage'))
const PlanDetailPage = lazy(() => import('@/pages/PlanDetailPage'))
const SongsPage = lazy(() => import('@/pages/SongsPage'))
const SongDetailPage = lazy(() => import('@/pages/SongDetailPage'))
const SongPrintPage = lazy(() => import('@/pages/SongPrintPage'))
const SchedulePage = lazy(() => import('@/pages/SchedulePage'))
const TeamsPage = lazy(() => import('@/pages/TeamsPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const NotificationSettingsPage = lazy(() => import('@/pages/NotificationSettingsPage'))
const AccountPage = lazy(() => import('@/pages/AccountPage'))
const AdminPage = lazy(() => import('@/pages/AdminPage'))
const RehearsePage = lazy(() => import('@/pages/RehearsePage'))
const SongBuilderPage = lazy(() => import('@/pages/SongBuilderPage'))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'))
const SignInPage = lazy(() => import('@/pages/SignInPage'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'))

/** Sends anyone below admin back to the dashboard rather than showing a wall of 403s. */
function RequireAdmin({ children }: { children: ReactNode }) {
  const role = useAuthStore((s) => s.user?.role)
  return role === 'admin' ? <>{children}</> : <Navigate to="/" replace />
}

export default function App() {
  const { state, error } = useBootstrap()
  const authStatus = useAuthStore((s) => s.status)
  const completeSso = useAuthStore((s) => s.completeSso)
  const attemptSilentSso = useAuthStore((s) => s.attemptSilentSso)
  const ssoEnabled = useAuthStore((s) => s.signInConfig?.ssoEnabled)
  const silentTried = useRef(false)
  const [showForgot, setShowForgot] = useState(false)
  const [ssoError, setSsoError] = useState<string | null>(null)

  /*
   * Finish a "Sign in with MyMDV" round trip.
   *
   * The provider redirects back to `/?sso=<one-time code>`. It is traded for a
   * real token immediately and stripped from the URL with replaceState, so it
   * never reaches the browser's history or a bookmark.
   */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('sso')
    const failure = params.get('sso_error')

    if (failure) {
      setSsoError(failure)
      window.history.replaceState({}, '', window.location.pathname)
      return
    }
    if (!code) return

    window.history.replaceState({}, '', window.location.pathname)
    void completeSso(code).then((ok) => {
      if (!ok) setSsoError('That sign-in could not be completed. Try again.')
    })
  }, [completeSso])

  /*
   * Already signed in to Hub means already signed in here.
   *
   * Runs once, only once the session check has said "anonymous" and only when
   * the server says SSO is configured. Guarded by a ref rather than state so a
   * re-render cannot start a second frame — two silent attempts race for one
   * single-use code, and the loser reports a failure that never happened.
   */
  useEffect(() => {
    if (authStatus !== 'anonymous' || !ssoEnabled || silentTried.current) return
    if (new URLSearchParams(window.location.search).has('sso')) return
    silentTried.current = true
    void attemptSilentSso()
  }, [authStatus, ssoEnabled, attemptSilentSso])

  /*
   * A reset link has to work whatever the session says — including for
   * somebody already signed in on another device, and before the bootstrap
   * has decided who they are.
   */
  const onResetRoute = window.location.pathname.replace(/\/+$/, '').endsWith('/reset-password')

  if (state === 'starting') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <LoadingState label="Opening your local database…" />
      </div>
    )
  }

  if (state === 'failed') {
    return (
      <div className="mx-auto flex min-h-screen max-w-lg items-center p-6">
        <ErrorState
          title="Services could not start"
          message={
            error ??
            'The local database could not be opened. Private browsing windows block IndexedDB in some browsers.'
          }
          onRetry={() => window.location.reload()}
        />
      </div>
    )
  }

  if (onResetRoute) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingState label="Loading…" />}>
          <ResetPasswordPage
            onDone={() => {
              // Straight to the door: the reset revoked every token, so
              // whatever session this tab had is gone.
              window.location.replace(import.meta.env.BASE_URL)
            }}
          />
        </Suspense>
      </ErrorBoundary>
    )
  }

  // Not signed in: the only thing rendered is the door.
  if (authStatus === 'anonymous' || authStatus === 'setup') {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingState label="Loading…" />}>
          {showForgot && authStatus === 'anonymous' ? (
            <ForgotPasswordPage onBack={() => setShowForgot(false)} />
          ) : (
            <SignInPage
              mode={authStatus === 'setup' ? 'setup' : 'signin'}
              onForgotPassword={() => setShowForgot(true)}
              ssoError={ssoError}
            />
          )}
        </Suspense>
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary>
      <Routes>
        <Route element={<AppShell />}>
          <Route
            index
            element={
              <Suspense fallback={<LoadingState />}>
                <DashboardPage />
              </Suspense>
            }
          />
          <Route path="plans" element={<Suspense fallback={<LoadingState />}><PlansPage /></Suspense>} />
          <Route path="plans/:planId" element={<Suspense fallback={<LoadingState />}><PlanDetailPage /></Suspense>} />
          <Route path="songs" element={<Suspense fallback={<LoadingState />}><SongsPage /></Suspense>} />
          <Route path="songs/:songId" element={<Suspense fallback={<LoadingState />}><SongDetailPage /></Suspense>} />
          <Route path="songs/:songId/print" element={<Suspense fallback={<LoadingState />}><SongPrintPage /></Suspense>} />
          <Route path="songs/:songId/builder" element={<Suspense fallback={<LoadingState />}><SongBuilderPage /></Suspense>} />
          <Route path="songs/:songId/rehearse" element={<Suspense fallback={<LoadingState />}><RehearsePage /></Suspense>} />
          <Route path="plans/:planId/rehearse" element={<Suspense fallback={<LoadingState />}><RehearsePage /></Suspense>} />
          <Route path="schedule" element={<Suspense fallback={<LoadingState />}><SchedulePage /></Suspense>} />
          <Route path="teams" element={<Suspense fallback={<LoadingState />}><TeamsPage /></Suspense>} />
          <Route path="notifications" element={<Suspense fallback={<LoadingState />}><NotificationsPage /></Suspense>} />
          <Route path="notifications/settings" element={<Suspense fallback={<LoadingState />}><NotificationSettingsPage /></Suspense>} />
          <Route path="account" element={<Suspense fallback={<LoadingState />}><AccountPage /></Suspense>} />
          <Route path="people" element={<Navigate to="/teams" replace />} />
          {/*
            Typing /admin should not open the admin screen for a member. The
            endpoints behind it refuse them anyway, but a page full of
            permission errors is not an answer — send them home instead.
          */}
          <Route
            path="admin"
            element={
              <RequireAdmin>
                <Suspense fallback={<LoadingState />}><AdminPage /></Suspense>
              </RequireAdmin>
            }
          />
          <Route path="*" element={<Suspense fallback={<LoadingState />}><NotFoundPage /></Suspense>} />
        </Route>
      </Routes>
      <HiddenSimulationWidget />
    </ErrorBoundary>
  )
}
